import type { LocalizedText } from '../types';

/**
 * Resolve a LocalizedText or plain string to the correct language.
 * Backward compatible: plain strings are returned as-is.
 */
export function resolveLocalized(value: string | LocalizedText | undefined, locale: string): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    return (value as any)[locale] || value.en || '';
  }
  return String(value);
}

/**
 * Get text from a LocalizedText for a specific language (for admin editing).
 */
export function getLocalizedText(value: string | LocalizedText | undefined, lang: string): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return lang === 'en' ? value : '';
  if (typeof value === 'object') {
    return (value as any)[lang] || '';
  }
  return '';
}

/**
 * Set text for a specific language in a LocalizedText, returning the updated object.
 */
export function setLocalizedText(
  current: string | LocalizedText | undefined,
  lang: string,
  newValue: string
): LocalizedText {
  if (typeof current === 'string') {
    // Migrate plain string: treat existing as 'en'
    const result: LocalizedText = { en: current };
    (result as any)[lang] = newValue;
    return result;
  }
  if (typeof current === 'object' && current !== null) {
    return { ...current, [lang]: newValue };
  }
  return { en: '', [lang]: newValue };
}

/**
 * Ensure a value is in LocalizedText format. If it's a plain string, wrap as { en: value }.
 */
export function ensureLocalizedText(value: string | LocalizedText | undefined): LocalizedText {
  if (value === undefined || value === null) return { en: '' };
  if (typeof value === 'string') return { en: value };
  return value;
}

/**
 * Check if a LocalizedText object is a localized object (not a plain string).
 */
export function isLocalizedText(value: any): value is LocalizedText {
  return typeof value === 'object' && value !== null && typeof value.en === 'string';
}
